// shadcn/ui
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

// Shared components
import { GenerateInvoiceIcon } from '@components/icons';

// Hooks
import { useTranslation } from '@hooks/useTranslation';

// Document availability + publishability (shared with the bulk documents modal)
import {
  getDocumentAvailability,
  expandDocumentKeys,
  type DocumentKey,
} from './documentAvailability';
import { isEventPublishable } from './eventPublishability';

/**
 * Canonical dropdown-item style, shared by the Events list row kebab and the
 * order event-card kebab so both menus look identical.
 */
export const MENU_ITEM_CLASS = cn(
  'relative isolate flex items-center gap-1.5 px-5 py-1.5 cursor-pointer font-inter text-sm font-semibold',
  'hover:bg-transparent focus:bg-transparent data-[highlighted]:bg-transparent',
  'before:absolute before:inset-y-0 before:left-3 before:right-3 before:-z-10 before:rounded-xs',
  'hover:before:bg-dinnerplate-white-light data-[highlighted]:before:bg-dinnerplate-white-light'
);

/** Event-order options in the Generate menu, in display order. */
const GENERATE_OPTION_KEYS: readonly DocumentKey[] = [
  'production_and_fulfilment',
  'display_copy',
  'timestamp',
  'dish_label',
  'menu_card',
];

export interface EventOrderGenerateMenuItemsProps {
  /** Normalized event type key (ApiEvent.event_type): 'buffet' | 'mini_buffet' | … */
  eventTypeKey: string;
  productLine?: string | null;
  /** Event status, used for the publishability gate. */
  eventStatus: string;
  /** Called with the resolved (wedding-expanded) document types for the chosen option. */
  onGenerate: (documentTypes: string[]) => void;
  /** Render a leading separator above the Generate header (default true). */
  leadingSeparator?: boolean;
  /** Generating event-order docs is event_orders.generate-gated (server 403s otherwise); required so no caller forgets it. */
  canGenerate: boolean;
}

/**
 * The "Generate" section of an event kebab: a header plus the five event-order
 * document options. Options are gated by the event's normalized type (buffet vs
 * box) and by publishability — drafts and cancelled events can't produce event
 * orders. Non-applicable options are disabled with a reason tooltip.
 *
 * Shared by the Events list row kebab (`EventRowActions`) and the order
 * event-card kebab (`EventsSectionCard`) so their gating and wedding routing
 * stay identical by construction. Wedding routing lives in `expandDocumentKeys`:
 * Timestamp and Display copy each emit *both* the default and the `wedding_*`
 * variant; the backend routes each event to the right one. The component sends
 * `expandDocumentKeys([key])`, never `[key]`.
 *
 * Renders `DropdownMenuItem`s — must be placed inside a `DropdownMenuContent`.
 */
export function EventOrderGenerateMenuItems({
  eventTypeKey,
  productLine,
  eventStatus,
  onGenerate,
  leadingSeparator = true,
  canGenerate,
}: EventOrderGenerateMenuItemsProps) {
  const { t } = useTranslation();
  const availability = getDocumentAvailability([{eventType: eventTypeKey, productLine}]);
  const publishable = isEventPublishable(eventStatus);

  if (!canGenerate) return null;

  return (
    <>
      {leadingSeparator && <DropdownMenuSeparator className="mx-2 my-3 bg-dinnerplate-white" />}
      <p className="px-5 font-inter text-sm font-semibold text-grey leading-[1.5] mb-1.5">
        {t('row_actions.generate')}
      </p>
      {GENERATE_OPTION_KEYS.map((key) => {
        const disabled = !publishable || availability[key].disabled;
        const reasonKey = !publishable
          ? 'row_actions.not_publishable_reason'
          : availability[key].reasonKey
            ? `download_modal.${availability[key].reasonKey}`
            : undefined;

        return (
          <DropdownMenuItem
            key={key}
            disabled={disabled}
            title={disabled && reasonKey ? t(reasonKey) : undefined}
            onSelect={() => onGenerate(expandDocumentKeys([key]))}
            className={MENU_ITEM_CLASS}
          >
            <GenerateInvoiceIcon className="size-3.5" />
            {t(`download_modal.${key}`)}
          </DropdownMenuItem>
        );
      })}
    </>
  );
}
